import { jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useRef, useState, } from 'react';
import { ImageRenderMode } from '../../enums';
import { canUseIntersectionObserver, useIntersection } from '../../hooks';
const LazyLoadedImage = ({ alt, decoding = 'auto', src, ...restImageProps }) => {
    /**
     * Four different states for image rendering
     * ---
     * Server side rendering:
     * 1. Always render image within a <noscript> tag
     *
     * Client side rendering (render according to priority):
     * 1. Lazy loading attribute is supported -> render image relying on "loading: lazy"
     * 2. Intersectionobserver is available -> render image conditionally based on isIntersecting
     * 3. IntersectionObserver is not available -> render image without lazy loading
     *
     * NOTE: for "renderMode: intersectionObserver" it is not possible to render with <noscript> for we
     * are depending on the imageRef, hence the "src" conditional.
     */
    const [renderMode, setRenderMode] = useState(ImageRenderMode.Server);
    const [isInViewport, setTargetImage] = useIntersection({
        rootMargin: '200px', // extend area to intersect earlier
        threshold: 0, // first visible pixel
        triggerOnce: true,
    });
    const imageRef = useRef(null);
    useEffect(() => {
        let tempRenderMode = ImageRenderMode.Fallback;
        if ('loading' in HTMLImageElement.prototype) {
            tempRenderMode = ImageRenderMode.NativeLazyLoad;
        }
        else if (canUseIntersectionObserver()) {
            tempRenderMode = ImageRenderMode.IntersectionObserver;
        }
        setRenderMode(tempRenderMode);
    }, [setRenderMode]);
    useEffect(() => {
        if (renderMode === ImageRenderMode.IntersectionObserver && imageRef.current) {
            setTargetImage(imageRef.current);
        }
    }, [imageRef, renderMode, setTargetImage]);
    // Always render src unless intersection-observer is used and element is not visible
    const shouldRenderSrc = !(renderMode === ImageRenderMode.IntersectionObserver && !isInViewport);
    const ImageComponent = (_jsx("img", { alt: alt, decoding: decoding, "data-src": shouldRenderSrc ? '' : src, loading: "lazy" // will only work if supported in the browser, renderMode: nativeLazyLoad
        , src: shouldRenderSrc ? src : '', ref: imageRef, role: "presentation", ...restImageProps }));
    return renderMode === ImageRenderMode.Server ? (_jsx("noscript", { children: ImageComponent })) : (ImageComponent);
};
export { LazyLoadedImage };
